import { Injectable } from '@angular/core';
import { faIdCard } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
	IDropDownMenuItem,
	IMenuItem,
	PluginInjectorApplicationPlugin,
	PluginInjectorService,
	IAppDashboardMenu,
	IMenuItemPermissions
} from 'core-client/api';
import { PLUGIN_MODULE_PERMISSIONS } from '../shared/permissions.constant';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private navbarMenuItem!: IMenuItem;
  private readonly navbarMenuTitle: string = 'DASHBOARD';
  private readonly pluginModule: string = 'DASHBOARD';
  private menuItemPermissions: IMenuItemPermissions;

  constructor(private readonly pluginInjectorService: PluginInjectorService) {
    this.menuItemPermissions = {
      dashboard: JSON.parse(JSON.stringify(PLUGIN_MODULE_PERMISSIONS.dashboard))
    };
  }

  getNavbarMenuItem(
      pluginConfig: PluginInjectorApplicationPlugin.IApplicationPlugin,
      dashboardMenu: IMenuItem[],
      primaryEnabled: boolean,
      secondaryEnabled: boolean
  ): IMenuItem[] {
    const menuItemsPermissionsRefs = this.getMenuItemPermissionRefs();

    const menuExist = dashboardMenu.some(
        (menuItem: IAppDashboardMenu) =>
            menuItem.nameIdentifier.toLowerCase() === (this.navbarMenuTitle).toLowerCase()
    );

    this.navbarMenuItem = {
      pluginId: pluginConfig._id,
      pluginName: pluginConfig.name,
      pluginModule: this.pluginModule,
      nameIdentifier: this.navbarMenuTitle,
      accessRights: menuItemsPermissionsRefs,
      primary: {
        enabled: primaryEnabled,
        position: dashboardMenu.length + 1
      },
      secondary: {
        enabled: secondaryEnabled,
        position: dashboardMenu.length + 1
      },
      strictMode: true,
      headerSortOrder: 30,
      icon: faIdCard,
      dropdown: true,
      dropdownItems: [
        this.createMenuViewDashboard(
            'VIEW_DASHBOARD',
            pluginConfig.name,
            'core_plugin_dashboard_custom_dashboard_list'
        ),
        this.createMenuCreateDashboard(
            'NEW_DASHBOARD',
            pluginConfig.name,
            ['core_plugin_dashboard_custom_dashboard_create', 'core_plugin_dashboard_standard_dashboard_create']
        ),
        this.createMenuEditDashboard(
            'EDIT_DASHBOARD',
            pluginConfig.name,
            ['core_plugin_dashboard_custom_dashboard_edit', 'core_plugin_dashboard_standard_dashboard_edit']
        ),
        this.createMenuDeleteDashboard(
            'DELETE_DASHBOARD',
            pluginConfig.name,
            ['core_plugin_dashboard_custom_dashboard_delete', 'core_plugin_dashboard_standard_dashboard_delete']
        )
      ],
      disabled: new BehaviorSubject(false)
    };

    if (!menuExist) {
      dashboardMenu.push(this.navbarMenuItem);
    }

    return dashboardMenu;
  }

  private getMenuItemPermissionRefs(): string[] {
    let menuItemsPermissionsRefs: string[] = [];

    for (const menuItemFlag in this.menuItemPermissions) {
      const menuItemsKeys = Object.values(this.menuItemPermissions[menuItemFlag]);

      menuItemsPermissionsRefs = [...new Set(menuItemsKeys.flatMap((obj) =>
          Object.entries(obj)
          .filter(([key, value]) => value === false)
          .map(([key]) => key)
      ))];
    }

    return menuItemsPermissionsRefs;
  }

  getMenuItemPermissions(): IMenuItemPermissions {
    return this.menuItemPermissions;
  }

  private createMenuViewDashboard(
      nameIdentifier: string,
      pluginName: string,
      accessReference: string | string[],
      width?: string
  ): IDropDownMenuItem {
    return {
      pluginName: pluginName,
      pluginModule: this.pluginModule,
      nameIdentifier,
      accessReference,
      strictMode: true,
      icon: faIdCard,
      dropdown: true,
      dropdownItems: [],
      disabled: new BehaviorSubject(false),
      callback: async (dialog: MatDialog, id?: string) => {
        const lazyComponent = await import('./navbar-menu-item/view-dashboard/view-dashboard.component');
        const registeredPlugin = this.pluginInjectorService.getPlugins().find(
            (plugin: PluginInjectorApplicationPlugin.IApplicationPlugin) => plugin.name === pluginName
        );
        const dialogConfig = this.pluginInjectorService.getMenuDialogConfig();
        const dialogRef= dialog.open(lazyComponent.ViewDashboardComponent, {
          ...dialogConfig,
          data: {
            id: id,
            pluginId: registeredPlugin?._id,
            pluginModule: this.pluginModule
          }
        });
        this.pluginInjectorService.setMenuDialogRef(dialogRef);
      }
    };
  };

  private createMenuCreateDashboard(
      nameIdentifier: string,
      pluginName: string,
      accessReference: string | string[], width?: string
  ): IDropDownMenuItem {
    return {
      pluginName: pluginName,
      pluginModule: this.pluginModule,
      nameIdentifier,
      accessReference,
      strictMode: true,
      icon: faIdCard,
      dropdown: true,
      dropdownItems: [],
      disabled: new BehaviorSubject(false),
      callback: async (dialog: MatDialog, id?: string) => {
        const lazyComponent = await import('./navbar-menu-item/new-dashboard/new-dashboard.component')
        const registeredPlugin = this.pluginInjectorService.getPlugins().find(
            (plugin: PluginInjectorApplicationPlugin.IApplicationPlugin) => plugin.name === pluginName
        );
        const dialogConfig = this.pluginInjectorService.getMenuDialogConfig();

        const dialogRef = dialog.open(lazyComponent.NewDashboardComponent, {
          ...dialogConfig,
          data: {
            id: id,
            pluginId: registeredPlugin?._id,
            pluginModule: this.pluginModule
          }
        });
        this.pluginInjectorService.setMenuDialogRef(dialogRef);
      }
    };
  };

  private createMenuEditDashboard(
      nameIdentifier: string,
      pluginName: string,
      accessReference: string | string[],
      width?: string
  ): IDropDownMenuItem {
    return {
      pluginName: pluginName,
      pluginModule: this.pluginModule,
      nameIdentifier,
      accessReference,
      strictMode: true,
      icon: faIdCard,
      dropdown: true,
      dropdownItems: [],
      disabled: new BehaviorSubject(false),
      callback: async (dialog: MatDialog, id?: string) => {
        const lazyComponent = await import('./navbar-menu-item/edit-dashboard/edit-dashboard.component');
        const registeredPlugin = this.pluginInjectorService.getPlugins().find(
            (plugin: PluginInjectorApplicationPlugin.IApplicationPlugin) => plugin.name === pluginName
        );
        const dialogConfig = this.pluginInjectorService.getMenuDialogConfig();
        const dialogRef = dialog.open(lazyComponent.EditDashboardComponent, {
          ...dialogConfig,
          data: {
            id: id,
            pluginId: registeredPlugin?._id,
            pluginModule: this.pluginModule
          }
        });
        this.pluginInjectorService.setMenuDialogRef(dialogRef);
      }
    };
  };

  private createMenuDeleteDashboard(
      nameIdentifier: string,
      pluginName: string,
      accessReference: string | string[],
      width?: string
  ): IDropDownMenuItem {
    return {
      pluginName: pluginName,
      pluginModule: this.pluginModule,
      nameIdentifier,
      accessReference,
      strictMode: true,
      icon: faIdCard,
      dropdown: true,
      dropdownItems: [],
      disabled: new BehaviorSubject(false),
      callback: async (dialog: MatDialog, id?: string) => {
        const lazyComponent = await import('./navbar-menu-item/delete-dashboard/delete-dashboard.component');
        const registeredPlugin = this.pluginInjectorService.getPlugins().find(
            (plugin: PluginInjectorApplicationPlugin.IApplicationPlugin) => plugin.name === pluginName
        );
        const dialogConfig = this.pluginInjectorService.getMenuDialogConfig();
        const dialogRef = dialog.open(lazyComponent.DeleteDashboardComponent, {
          ...dialogConfig,
          data: {
            id: id,
            pluginId: registeredPlugin?._id,
            pluginModule: this.pluginModule
          }
        });
        this.pluginInjectorService.setMenuDialogRef(dialogRef);
      }
    };
  };
}
